<template>
  <Layout relative>
    <Banner :alert="notification" @close="onBannerClose" />
    <loading
      :active.sync="isBeingSaved"
      :can-cancel="false"
      :is-full-page="true"
      background-color="#f0f0f0"
      loader="bars"
    >
    </loading>
    <Flex grow column border>
      <Flex border-b-2 border-primary-200>
        <Flex :fill="true">
          <div class="font-bold capitalize opacity-50 w-tabs items-center px-2 text-lg">
            <span>{{ formName }}</span>
          </div>
        </Flex>
        <Flex>
          <Button color="primary" class="save-button" @click="onSaveClick" append-icon="save"
            ><span class="save-button-label">SAVE</span>
          </Button>
        </Flex>
      </Flex>
      <Flex border-b-2 border-primary-200 v-if="parentForms.length">
        <StateParent
          :parent-forms="parentForms"
          :selectedForm="selectedForm"
          :isDefinedInMaster="isDefinedInMaster"
          @update:isDefinedInMaster="setDefinedInMaster"
          @update:selectedForm="setSelectedForm"
        ></StateParent>
      </Flex>
      <Flex state-designer h-full vld-parent overflow-auto>
        <loading
          :active="isDefinedInMaster === true || isDefinedInMaster === null"
          :can-cancel="false"
          :is-full-page="false"
          background-color="#f0f0f0"
          v-bind:height="0"
          v-bind:width="0"
        >
        </loading>
        <Panel span="2" h-full>
          <Layout>
            <StateList
              :root="root"
              :selectedListItem.sync="selectedStateIndex"
              :add-state="addState"
              :invalid-statuses="getInvalidStatusIndexes()"
              :async-invalid-statuses="invalidAsyncStatuses"
            ></StateList>
          </Layout>
        </Panel>
        <Panel span="10" h-full editor-placeholder>
          <template v-if="isStateSelected()">
            <StateEditor
              :selectedStateIndex="selectedStateIndex"
              :node="root.children[selectedStateIndex]"
              :base-states="baseStates"
              :component-id="componentId"
              :is-valid-xml="isValidXml"
              @removeState="removeState"
            ></StateEditor>
          </template>
          <template v-else>
            <Layout center> Select a state to edit </Layout>
          </template>
        </Panel>
      </Flex>
    </Flex>
  </Layout>
</template>

<script>
import Flex from '@wizer/core/components/flex/Flex'
import Layout from '@wizer/core/components/layout/Layout'
import Panel from '@wizer/core/components/panel/Panel'
import Banner from '@wizer/core/components/banner/Banner'
import { mapActions, mapState } from 'vuex'
import _ from 'lodash'
import { useTree } from '@wizer/core/composables'
import Loading from 'vue-loading-overlay'
import Button from '@wizer/core/components/button/Button'
import StateList from '@/components/designer/StateList'
import StateParent from '@/components/designer/StateParent'
import StateEditor from '@/components/designer/StateEditor'
import TitleMixin from '@/mixins/TitleMixin'
import FormStateService from '@/services/FormStateService'
import ConditionService from '@/services/ConditionService'
import UniqueId from '@/utils/UniqueIdHelper'

export default {
  name: 'StateDesigner',
  title: 'Workflow Designer',

  components: {
    StateParent,
    Flex,
    StateEditor,
    StateList,
    Layout,
    Panel,
    Banner,
    Loading,
    Button,
  },
  mixins: [TitleMixin],
  data() {
    return {
      selectedStateIndex: null,
      baseStates: [],
      parentForms: [],
      selectedForm: null,
      isDefinedInMaster: null,
      componentId: null,
      formName: null,
      invalidAsyncStatuses: [],
      isBeingSaved: false,
    }
  },
  async mounted() {
    await this.loadStateData()
  },
  methods: {
    setDefinedInMaster(value) {
      this.isDefinedInMaster = value
      if (!value) {
        this.selectedForm = null
      }
    },

    setSelectedForm(value) {
      this.selectedForm = value
    },

    async onSaveClick() {
      if (this.selectedForm) {
        this.saveParentFormData()
      } else {
        this.saveFormState()
      }
    },
    async saveFormState() {
      let notification
      this.isBeingSaved = true
      // To be used with the save endpoint
      const formStates = this.root.data.children.map((node, index) => ({
        id: UniqueId.isANewRecord(node.id) ? '' : node.id,
        label: node.label,
        description: node.description,
        sequenceNumber: index + 1,
        mappedState: {
          id: node.baseState,
        },
        isActive: node.isActive,
        workflowCondition: {
          conditionText: node.workflowConditionText,
          id: node.workflowConditionId,
        },
        visibilityCondition: {
          conditionText: node.visibilityConditionText,
          id: node.visibilityConditionId,
        },
        visibilityOption: node.visibilityOption,
      }))
      const data = {
        formStates,
        formId: this.$route.params.id,
      }
      notification = {
        type: 'success',
        message: 'Statuses have been saved successfully',
        duration: this.notificationDuration,
      }
      // Validate
      try {
        this.invalidAsyncStatuses.length = 0
        // Synchronous validations
        if (this.getInvalidStatusIndexes().length) {
          notification = {
            type: 'error',
            message: 'Validation errors detected',
            duration: this.notificationDuration,
          }
          return
        }
        // Asynchronous validations
        const invalidConditions = await this.getInvalidConditions()
        if (invalidConditions.length) {
          this.invalidAsyncStatuses.push(...invalidConditions)
          notification = {
            type: 'error',
            message: 'Failed to convert XML',
            duration: this.notificationDuration,
          }
          return
        }
        // Validations passed, proceed with saving form statuses
        const responseData = await FormStateService.saveFormStates(data)
        this.updateStatusesFromResponse(responseData)
      } catch (error) {
        const message = error.response ? error.response.data : error
        notification = { type: 'error', message, duration: this.notificationDuration }
      } finally {
        this.isBeingSaved = false
        await this.$store.dispatch('Notification/add', notification, { root: true })
      }
    },
    async saveParentFormData() {
      let notification = {
        type: 'success',
        message: 'Parent form data has been saved successfully',
        duration: this.notificationDuration,
      }
      this.isBeingSaved = true
      const data = {
        formId: this.$route.params.id,
        parentFormId: this.selectedForm,
      }
      try {
        await FormStateService.saveParentFormData(data)
      } catch (error) {
        const message = error.response ? error.response.data : error
        notification = { type: 'error', message, duration: this.notificationDuration }
      } finally {
        this.isBeingSaved = false
        await this.$store.dispatch('Notification/add', notification, { root: true })
      }
    },
    updateStatusesFromResponse(responseData) {
      const self = this
      responseData.data.forEach((savedState) => {
        const seqNr = savedState.sequenceNumber
        self.root.children[seqNr - 1].update((data) => {
          data.id = savedState.id
          data.workflowConditionId = savedState.workflowCondition.id
          data.visibilityConditionId = savedState.visibilityCondition
            ? savedState.visibilityCondition.id
            : ''
        })
      })
    },
    async getInvalidConditions() {
      const bulkConditionData = (
        await ConditionService.getBulkConditionDataJSON(
          this.root.children.map((node) => {
            return {
              xmlCondition: node.data.workflowConditionText,
              formId: this.$route.params.id,
            }
          }),
        )
      ).data
      // Filter out indexes of the failed conditions
      return bulkConditionData
        .map((conditionData, index) => {
          return { conditionData, index }
        })
        .filter((condition) => !condition.conditionData.children)
        .map((condition) => {
          return condition.index
        })
    },
    async loadStateData() {
      const formStateResponseData = (await FormStateService.getFormStates(this.$route.params.id))
        .data
      const formStates = formStateResponseData.formStates
      this.componentId = formStateResponseData.componentId
      this.formName = formStateResponseData.formName
      this.selectedForm = formStateResponseData.selectedForm
      this.isDefinedInMaster =
        formStateResponseData.parentForms.filter((w) => {
          return w.id === formStateResponseData.selectedForm
        }).length > 0
      this.baseStates = formStateResponseData.baseStates.map((baseState) => ({
        data: { value: baseState.id, label: baseState.label },
      }))
      this.parentForms = formStateResponseData.parentForms.map((parentForm) => ({
        data: { value: parentForm.id, label: parentForm.label },
      }))
      const mappedFormStates = formStates
        .map((formState) => ({
          __typename: 'State',
          id: formState.id,
          label: formState.label,
          description: formState.description,
          workflowConditionText: formState.workflowCondition
            ? formState.workflowCondition.conditionText
            : '',
          visibilityConditionText: formState.visibilityCondition
            ? formState.visibilityCondition.conditionText
            : '',
          workflowConditionId: formState.workflowCondition ? formState.workflowCondition.id : '',
          visibilityConditionId: formState.visibilityCondition
            ? formState.visibilityCondition.id
            : '',
          baseState: formState.mappedState.id,
          sequenceNumber: formState.sequenceNumber,
          isActive: formState.isActive,
          visibilityOption: formState.visibilityOption,
        }))
        .sort((a, b) => b.sequenceNumber - a.sequenceNumber)

      mappedFormStates.forEach((formState) => this.root.add(formState))
    },
    isValidXml(inputString) {
      const parser = new DOMParser()
      const parserErrorNS = parser
        .parseFromString('INVALID', 'application/xml')
        .getElementsByTagName('parsererror')[0].namespaceURI
      const dom = parser.parseFromString(inputString, 'application/xml')
      return dom.getElementsByTagNameNS(parserErrorNS, 'parsererror').length <= 0
    },
    removeState() {
      this.root.children[this.selectedStateIndex].remove()
      this.selectedStateIndex = null
    },
    isStateSelected() {
      return !_.isNil(this.selectedStateIndex)
    },
    onBannerClose() {
      this.removeNotification(this.notifications.slice(-1)[0])
    },
    getInvalidStatusIndexes() {
      const invalidStatuses = []
      if (!this.root || !this.root.children) {
        return invalidStatuses
      }
      for (let i = 0; i < this.root.children.length; i++) {
        const stateNode = this.root.children[i]
        const nodeData = stateNode.data
        if (!nodeData.label) {
          invalidStatuses.push(i)
        }
        if (!nodeData.baseState) {
          invalidStatuses.push(i)
        }
        if (!nodeData.visibilityOption) {
          invalidStatuses.push(i)
        }
        if (
          !nodeData.workflowConditionText ||
          (nodeData.visibilityOption !== 'always' && !nodeData.visibilityConditionText)
        ) {
          invalidStatuses.push(i)
        }
        if (
          !this.isValidXml(nodeData.workflowConditionText) ||
          (nodeData.visibilityOption !== 'always' &&
            !this.isValidXml(nodeData.visibilityConditionText))
        ) {
          invalidStatuses.push(i)
        }
      }
      return invalidStatuses
    },
    ...mapActions({ removeNotification: 'Notification/remove' }),
  },
  computed: {
    notification() {
      if (!this.notifications.length) return null
      return this.notifications.slice(-1)[0]
    },
    ...mapState({
      notifications: (state) => state.Notification.notifications,
      notificationDuration: (state) => state.Notification.notificationDuration,
    }),
  },
  watch: {
    $route() {
      this.$router.go()
    },
  },
  setup() {
    const statesData = {
      __typename: 'States',
      id: 'States',
      label: 'States',
      children: [],
    }

    const statesSchema = {
      States: {
        children: {
          type: ['State'],
        },
      },
    }
    const { root } = useTree(statesData, {
      schema: statesSchema,
      ordered: true,
    })

    function addState(node) {
      const uniqueId = _.uniqueId()
      const newStateName = `Untitled ${uniqueId}`
      const newStateId = `uniqueId ${uniqueId}`
      const addMethod = _.last(node.children) ? _.last(node.children).addAfter : node.add
      addMethod({
        __typename: 'State',
        id: newStateId,
        label: newStateName,
        description: '',
        workflowConditionText: '',
        visibilityConditionText: '',
        workflowConditionId: '',
        visibilityConditionId: '',
        visibilityOption: '',
        baseState: '',
        isActive: true,
      })
    }

    return {
      root,
      addState,
    }
  },
}
</script>
<style lang="postcss" scoped>
.editor-placeholder {
  @apply text-gray-700 text-sm;
}
.save-button {
  @apply m-1;
}
.save-button-label {
  @apply mr-1;
}
</style>
